import { RefObject, useEffect } from 'react';

/**
 * Sends focus back to the body when an element is hidden while it has it.
 *
 * Belongs with the shortcuts because it exists for them: the registry hears
 * keys through the document, and a cross-origin iframe with focus keeps
 * every key inside itself. Hiding the iframe does not change that. Measured
 * in Edge on 2026-09-27: after a click inside a frame, `display: none` on
 * it - which is all Foundation's `hide` class is - left `activeElement` on
 * the IFRAME and every keydown inside it; `blur()` on the frame element put
 * focus on the body and the keys came back.
 *
 * The case this is for: a YouTube video that was clicked on ends, the next
 * track is a Drive file, the embed is hidden - and space does nothing until
 * something else on the page is clicked. Nothing is done while the element
 * is visible; a focused YouTube embed keeps its own keys on purpose.
 */
export default function useBlurWhenHidden(ref: RefObject<HTMLElement | null>, hidden: boolean) {
  useEffect(
    function blurWhenHidden() {
      const el = ref.current;

      if (hidden && el && document.activeElement === el) {
        el.blur();
      }
    },
    [ref, hidden],
  );
}
